import { useRef, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { UseFormSetValue } from "react-hook-form";
import type { Geofence } from "../../../../../../../store/admin/geofencesStore";
import type { ThemeClasses, PointCoordinate } from "../../types";
import { LINEAR_BUFFER_MIN, LINEAR_BUFFER_MAX } from "../../types";
import { CoordinateInput } from "./CoordinateInput";

interface EndpointAddressInputProps {
  value: string;
  placeholder: string;
  disabled: boolean;
  isDarkMode: boolean;
  themeClasses: ThemeClasses;
  onCommit: (address: string) => void;
}

function EndpointAddressInput({
  value,
  placeholder,
  disabled,
  isDarkMode,
  themeClasses,
  onCommit,
}: EndpointAddressInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const pickedFromDropdown = useRef(false);

  const commitAddress = useCallback(
    (address: string) => {
      if (address.trim() && address.trim() !== (value ?? "").trim()) {
        onCommit(address);
      }
    },
    [onCommit, value]
  );

  useEffect(() => {
    const input = inputRef.current;
    if (
      !input ||
      typeof google === "undefined" ||
      !google.maps?.places?.Autocomplete
    )
      return;

    if (autocompleteRef.current) return;

    const ac = new google.maps.places.Autocomplete(input, {
      types: ["geocode", "establishment"],
      fields: ["formatted_address"],
    });

    ac.addListener("place_changed", () => {
      const place = ac.getPlace();
      if (!place?.formatted_address) return;
      pickedFromDropdown.current = true;
      if (inputRef.current) inputRef.current.value = place.formatted_address;
      commitAddress(place.formatted_address);
    });

    autocompleteRef.current = ac;

    return () => {
      google.maps.event.clearInstanceListeners(ac);
      autocompleteRef.current = null;
    };
  }, [commitAddress]);

  const handleBlur = () => {
    if (pickedFromDropdown.current) {
      pickedFromDropdown.current = false;
      return;
    }
    commitAddress(inputRef.current?.value ?? "");
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.code !== "Enter" && event.code !== "NumpadEnter") return;
    event.preventDefault();
    inputRef.current?.blur();
  };

  return (
    <input
      ref={inputRef}
      type="text"
      defaultValue={value}
      placeholder={placeholder}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      disabled={disabled}
      className={`w-full text-xs 2xl:text-sm px-2 py-1.5 rounded-md border ${themeClasses.cardBorder} ${
        isDarkMode ? "bg-dark-surface text-gray-200" : "bg-white text-gray-800"
      } focus:outline-none focus:ring-2 focus:ring-primary-500 placeholder:text-gray-400 placeholder:italic disabled:opacity-50`}
    />
  );
}

interface LinearCoordinatesFormProps {
  linearPoints: PointCoordinate[];
  setLinearPoints: (points: PointCoordinate[]) => void;
  setValue: UseFormSetValue<Geofence>;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  buffer: number;
  onBufferChange: (value: number) => void;
  linearAddresses: { address: string | null; geocoding: boolean }[];
  onAddressEdit: (index: number, address: string) => void;
}

const ENDPOINT_KEYS = ["start", "end"] as const;

export function LinearCoordinatesForm({
  linearPoints,
  setLinearPoints,
  setValue,
  themeClasses,
  isDarkMode,
  buffer,
  onBufferChange,
  linearAddresses,
  onAddressEdit,
}: LinearCoordinatesFormProps) {
  const { t } = useTranslation("adminSections");

  const endpointLabels: Record<(typeof ENDPOINT_KEYS)[number], string> = {
    start: t("geofences.linear.startPoint", "Punto inicial"),
    end: t("geofences.linear.endPoint", "Punto final"),
  };

  const endpointStyles: Record<(typeof ENDPOINT_KEYS)[number], string> = {
    start: "bg-emerald-500",
    end: "bg-red-500",
  };

  const handleCoordinateChange = (
    index: number,
    key: keyof PointCoordinate,
    next: number
  ) => {
    const newPoints = linearPoints.map((point, i) =>
      i === index ? { ...point, [key]: next } : point
    );
    setLinearPoints(newPoints);
    setValue("coordinates", newPoints);
  };

  return (
    <div className="flex flex-col gap-3">
      <label className={`block text-sm font-medium ${themeClasses.text} mb-1`}>
        {t("geofences.linear.endpoints", "Puntos del corredor")}
      </label>

      <div className="space-y-2">
        {ENDPOINT_KEYS.map((key, idx) => {
          const point = linearPoints[idx];
          if (!point) return null;

          const entry = linearAddresses[idx];

          return (
            <div
              key={key}
              className={`flex flex-col gap-2 p-2 rounded-lg ${
                isDarkMode ? "bg-dark-card" : "bg-gray-50"
              } border ${themeClasses.cardBorder}`}
            >
              <div className="flex gap-2 items-center">
                <div
                  className={`flex-shrink-0 w-2.5 h-2.5 rounded-full ${endpointStyles[key]}`}
                  title={endpointLabels[key]}
                />
                <span
                  className={`flex-shrink-0 w-24 text-xs 2xl:text-sm ${themeClasses.textSecondary}`}
                >
                  {endpointLabels[key]}
                </span>

                <div className="flex-1 min-w-0 relative">
                  {entry?.geocoding && (
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 inline-block w-3 h-3 border-2 border-gray-300 border-t-primary-500 rounded-full animate-spin" />
                  )}
                  <EndpointAddressInput
                    value={entry?.address ?? ""}
                    placeholder={t(
                      "geofences.linear.addressPlaceholder",
                      "Escribe una dirección..."
                    )}
                    disabled={!!entry?.geocoding}
                    isDarkMode={isDarkMode}
                    themeClasses={themeClasses}
                    onCommit={(address) => onAddressEdit(idx, address)}
                  />
                </div>
              </div>

              <div className="flex gap-2 items-center pl-[7.5rem]">
              <div className="flex-1">
                <CoordinateInput
                  value={point.latitude}
                  onChange={(next) =>
                    handleCoordinateChange(idx, "latitude", next)
                  }
                  className={`w-full text-xs 2xl:text-base px-3 py-2 ${
                    isDarkMode ? "bg-dark-surface" : "bg-white"
                  } border ${themeClasses.cardBorder} rounded-lg ${
                    themeClasses.text
                  } focus:outline-none focus:ring-2 focus:ring-primary-500`}
                  ariaLabel={`${endpointLabels[key]} latitud`}
                />
              </div>

              <div className="flex-1">
                <CoordinateInput
                  value={point.longitude}
                  onChange={(next) =>
                    handleCoordinateChange(idx, "longitude", next)
                  }
                  className={`w-full text-xs 2xl:text-base px-3 py-2 ${
                    isDarkMode ? "bg-dark-surface" : "bg-white"
                  } border ${themeClasses.cardBorder} rounded-lg ${
                    themeClasses.text
                  } focus:outline-none focus:ring-2 focus:ring-primary-500`}
                  ariaLabel={`${endpointLabels[key]} longitud`}
                />
              </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex flex-col gap-2 pt-2">
        <div className="flex items-center justify-between">
          <label className={`text-sm font-medium ${themeClasses.text}`}>
            {t("geofences.linear.bufferLabel", "Ancho del corredor")}
          </label>
          <span className={`text-xs 2xl:text-sm ${themeClasses.textSecondary}`}>
            {t("geofences.linear.bufferValue", {
              defaultValue: "{{buffer}} m a cada lado ({{total}} m de ancho)",
              buffer,
              total: buffer * 2,
            })}
          </span>
        </div>
        <input
          type="range"
          min={LINEAR_BUFFER_MIN}
          max={LINEAR_BUFFER_MAX}
          step={10}
          value={buffer}
          onChange={(e) => onBufferChange(Number(e.target.value))}
          className="w-full accent-primary-500"
        />
        <span className="text-xs text-gray-400">
          {t(
            "geofences.linear.bufferHelp",
            "Distancia máxima al trazado para considerar que la unidad va dentro del corredor."
          )}
        </span>
      </div>
    </div>
  );
}
